import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

const BASE = `${PROXY_URL}/api/email-templates`;

/** The TA buckets (mirrors the proxy's CATEGORIES). */
export const CATEGORIES = [
  'invite',
  'booking',
  'booking-page',
  'rejection',
  'rejection-later',
  'talent-pool',
  'checking-in',
  'internal',
  'outreach-inmail',
  'outreach-follow-up',
] as const;
export type Category = (typeof CATEGORIES)[number];

/**
 * The LinkedIn outreach templates (mirrors the proxy's OUTREACH_CATEGORIES): shown on
 * Talent Sourcing › Email Library, and NOT on the candidate Email Library (Sofia, Oct 19 2026).
 */
export const OUTREACH_CATEGORIES: Category[] = ['outreach-inmail', 'outreach-follow-up'];
export const isOutreachCategory = (category: Category) => OUTREACH_CATEGORIES.includes(category);
/** LinkedIn's follow-up goes into the same thread: it has no subject line. */
export const isSubjectlessOutreach = (category: Category) => category === 'outreach-follow-up';

export const CATEGORY_LABELS: Record<Category, string> = {
  invite: 'Invites',
  booking: 'Booking confirmations',
  'booking-page': 'Booking page',
  rejection: 'Rejections',
  'rejection-later': 'Rejection in later stages',
  'talent-pool': 'Talent pool',
  'checking-in': 'Checking in',
  internal: 'Internal — Slack messages to colleagues',
  'outreach-inmail': 'InMails — first message',
  'outreach-follow-up': 'InMail follow-ups',
};

/**
 * Categories that go to a COLLEAGUE over Slack rather than to a candidate by email. Mirrors the
 * proxy's INTERNAL_CATEGORIES. They carry no subject, use Slack mrkdwn, and are English-only.
 */
export const INTERNAL_CATEGORIES: Category[] = ['internal'];
/**
 * Categories whose entries are one-LINERS, not whole emails (mirrors the proxy's
 * SNIPPET_CATEGORIES). They carry no subject: a sentence on a web page has none.
 */
export const SNIPPET_CATEGORIES: Category[] = ['booking-page'];
export const isSnippetCategory = (category: Category) => SNIPPET_CATEGORIES.includes(category);
export const isInternalCategory = (category: Category) => INTERNAL_CATEGORIES.includes(category);

/** One-line description per category, shown under the section header. */
export const CATEGORY_HINTS: Record<Category, string> = {
  invite: 'Invitations to the next interview — one per stage.',
  booking:
    'Sent automatically the moment a candidate books, moves or cancels a slot. Nobody presses send on these — editing them changes what every candidate receives.',
  'booking-page':
    'What the candidate reads on the booking page itself, while picking a slot and after they have booked. One sentence per entry, no subject.',
  rejection: 'Rejections, per stage or reason.',
  'rejection-later':
    'What the Action Board’s Archive button sends. Editing these changes the real rejection emails.',
  'talent-pool': 'Keep a strong candidate warm for future roles.',
  'checking-in': 'Nudges & status: pending decision, booking reminders, reschedules, missing info.',
  internal:
    'Slack messages the board sends to colleagues — feedback nudges and panel asks. English only, no subject.',
  'outreach-inmail':
    'The first LinkedIn InMail to someone who never applied. Every draft keeps this wording; the agent writes only {personalLine} (always in, even if the template leaves it out), {rolePitch} and {whereLine}. Links: select words and press Link — {jobLink} and {bookingLink} work as link addresses. A paragraph whose value is missing (no salary on the posting, no booking link) is left out. Which template a role uses is picked in its step 1.',
  'outreach-follow-up':
    'What LinkedIn sends by itself if there is no answer. Filled in as written, no AI. No subject: it goes into the same thread.',
};

export const SCOPES = ['company', 'personal'] as const;
export type Scope = (typeof SCOPES)[number];
export const SCOPE_LABELS: Record<Scope, string> = { company: 'Company', personal: 'Personal' };

export interface TokenDef {
  name: string;
  label: string;
  desc: string;
}

const TOKEN_DEFS: Record<string, TokenDef> = {
  // Internal Slack messages. The composed blocks (slots, options, agenda, debrief, logistics) and
  // the sentence fragments are rendered by the board from live data — a timed agenda with
  // per-person conflict notes cannot be written as flat text, which is why they are tokens.
  opening: { name: 'opening', label: 'Opening line', desc: 'Greeting, and who it is from (you or the TA team)' },
  candidateName: { name: 'candidateName', label: 'Candidate full name', desc: "The candidate's full name, unformatted" },
  candidate: { name: 'candidate', label: 'Candidate', desc: 'Candidate name in bold, with the role if known' },
  what: { name: 'what', label: 'Which interview', desc: '"the Case Interview", or "the interview"' },
  interviewerNames: { name: 'interviewerNames', label: 'Interviewer names', desc: 'The people being written to, joined naturally' },
  candidateCount: { name: 'candidateCount', label: 'Candidate count', desc: 'How many candidates are waiting' },
  slotCount: { name: 'slotCount', label: 'Slot count', desc: 'How many options are offered' },
  moveCount: { name: 'moveCount', label: 'Meetings to move', desc: 'How many meetings would have to shift' },
  news: { name: 'news', label: 'News line', desc: 'How many candidates are waiting' },
  need: { name: 'need', label: 'Need line', desc: 'How many slots need freeing' },
  shifting: { name: 'shifting', label: 'Options line', desc: 'Whether any meetings would have to move' },
  close: { name: 'close', label: 'Closing line', desc: 'The ask at the end' },
  slots: { name: 'slots', label: 'Slot list', desc: 'The offered times, composed from the calendars' },
  options: { name: 'options', label: 'Chain options', desc: 'The final-round options, composed with times and names' },
  moveNote: { name: 'moveNote', label: 'Move note', desc: 'Whether the options need anyone to move a meeting' },
  agenda: { name: 'agenda', label: 'Agenda', desc: 'The booked chain, segment by segment' },
  debrief: { name: 'debrief', label: 'Debrief line', desc: 'The debrief slot, when there is one' },
  logistics: { name: 'logistics', label: 'Logistics', desc: 'Arrival contact and laptop reminder (onsite)' },
  day: { name: 'day', label: 'Day', desc: 'The interview day' },
  mode: { name: 'mode', label: 'Onsite or remote', desc: 'How the round runs' },
  checkIn: { name: 'checkIn', label: 'Check-in ask', desc: 'The onsite who-dials-in question' },
  candidateFirstName: { name: 'candidateFirstName', label: 'Candidate first name', desc: "The candidate's first name" },
  previousInterviewers: { name: 'previousInterviewers', label: 'Previous interviewers', desc: 'Who already interviewed them (Lever)' },
  nextInterviewers: { name: 'nextInterviewers', label: 'Next interviewers', desc: "Who they'll meet next (the link's interviewers)" },
  nextInterviewersWithRoles: {
    name: 'nextInterviewersWithRoles',
    label: 'Next interviewers with roles',
    desc: 'The same people, each with their job title — "Louisa (Talent Acquisition Manager) or Theresa (Talent Acquisition Lead)"',
  },
  // Booking confirmations. `details` and `manageLink` are POSITIONS, not values: the email
  // draws the facts block and the reschedule button itself, in one format, and the body only
  // says where they go. Leave either out and it is appended after the text.
  meeting: { name: 'meeting', label: 'Meeting type', desc: '"Interview", or "Feedback call" for a post-rejection call' },
  interviewers: { name: 'interviewers', label: 'Interviewers', desc: 'Who the candidate is booked with' },
  interviewersWithRoles: {
    name: 'interviewersWithRoles',
    label: 'Interviewers with roles',
    desc: 'Who they are booked with, each with their job title',
  },
  details: { name: 'details', label: 'Booking details', desc: 'The When / Duration / With / Where block' },
  manageLink: { name: 'manageLink', label: 'Reschedule button', desc: 'The reschedule-or-cancel button (never shown on a cancellation)' },
  panelSize: {
    name: 'panelSize',
    label: 'How many interviewers',
    desc: 'The number of people on the panel (always 1 on a round-robin link)',
  },
  caseSubmissionEmails: {
    name: 'caseSubmissionEmails',
    label: 'Case panel emails',
    desc: 'The case interviewers’ email addresses — where the candidate sends their completed take-home case',
  },
  rescheduleNote: {
    name: 'rescheduleNote',
    label: 'Reschedule sentence',
    desc: 'The line above that button. Kept out of the template because it differs for a case or final round, where the candidate cannot pick a new time themselves',
  },
  bookingLink: {
    name: 'bookingLink',
    label: 'Booking link',
    desc: 'Invites: the per-candidate booking URL. InMails: your own booking link (InMail Library › My booking link)',
  },
  duration: { name: 'duration', label: 'Duration (minutes)', desc: "Interview length from the link's config" },
  /*
    ⚠️ Labelled "Job being hired for", not "Role title". Read as the latter, someone put
    ({roleTitle}) after the interviewer names meaning "their job title", and an invite went out
    naming the vacancy as though it were the interviewer's role (Theresa, 24 Sep 2026). The
    token for that is `nextInterviewersWithRoles`.
  */
  roleTitle: {
    name: 'roleTitle',
    label: 'Job being hired for',
    desc: 'The vacancy the candidate applied to — NOT an interviewer’s job title',
  },
  stageName: { name: 'stageName', label: 'Stage name', desc: 'The target interview stage' },
  recruiterName: { name: 'recruiterName', label: 'Recruiter name', desc: 'Your first name' },
  // Later-stage rejections only. `feedback` is the written case feedback the board drafts from the
  // interview notes; `bookingLink` here is the feedback-CALL link, minted per candidate when the
  // rejection is approved — not an interview booking link.
  feedback: {
    name: 'feedback',
    label: 'Written feedback',
    desc: 'The case feedback drafted from the interview notes (case template only)',
  },
  // Outreach InMails (Talent Sourcing › Email Library). Everything but personalLine, rolePitch and
  // whereLine is filled in by code; those three are written by the agent for each person.
  greeting: { name: 'greeting', label: 'Greeting', desc: '"Hi" — or write your own greeting into your personal template' },
  signOff: { name: 'signOff', label: 'Sign-off', desc: '"Viele Grüße" / "Best" — or write your own into your personal template' },
  personalLine: {
    name: 'personalLine',
    label: 'Personal reason (AI)',
    desc: 'One or two sentences on why you write to THIS person, written by the agent from their profile',
  },
  rolePitch: { name: 'rolePitch', label: 'Role pitch (AI)', desc: 'One or two sentences about the role, written by the agent from the brief' },
  whereLine: {
    name: 'whereLine',
    label: 'Location & work model (AI)',
    desc: 'One sentence on where the role is based and how it works (office, hybrid, remote), written by the agent from the brief',
  },
  location: { name: 'location', label: 'Location', desc: 'Where the role is based, as the brief words it' },
  workModel: { name: 'workModel', label: 'Work model', desc: 'Remote / hybrid / on-site, as the brief words it' },
  salary: { name: 'salary', label: 'Salary range', desc: 'The range on the Lever posting, e.g. "60.000 bis 75.000 € brutto im Jahr"' },
  jobLink: { name: 'jobLink', label: 'Job ad link', desc: 'The public job ad of the Lever posting' },
};

// `slots` (slot-proposal), like `agenda`/`logistics`/`day` (final-confirmation), is deliberately
// NOT in the "/" token menu: those tokens are composed by the board for ONE template each, and
// every other invite renderer leaves unknown tokens verbatim — offering them here would let TA
// insert a literal `{slots}` into e.g. the pre-screen invite, which ships to candidates as-is.
// The slot-proposal body already carries `{slots}` (and the offer modal appends the times even if
// an edit removes it), so nothing is lost by keeping it out of the menu.

/** Variables offered in the "/" menu, per category (mirrors TOKENS_BY_CATEGORY). */
export const TOKENS_BY_CATEGORY: Record<Category, TokenDef[]> = {
  invite: [
    'candidateFirstName',
    'previousInterviewers',
    'nextInterviewers',
    'nextInterviewersWithRoles',
    'bookingLink',
    'duration',
    'roleTitle',
    'stageName',
    'recruiterName',
  ].map((n) => TOKEN_DEFS[n]),
  'booking-page': ['roleTitle', 'stageName', 'duration', 'panelSize', 'caseSubmissionEmails'].map(
    (n) => TOKEN_DEFS[n]
  ),
  booking: [
    'candidateFirstName',
    'roleTitle',
    'stageName',
    'meeting',
    'interviewers',
    'interviewersWithRoles',
    'duration',
    'details',
    'manageLink',
    'rescheduleNote',
  ].map((n) => TOKEN_DEFS[n]),
  'rejection-later': [
    'candidateFirstName',
    'previousInterviewers',
    'roleTitle',
    'recruiterName',
    'feedback',
    'bookingLink',
  ].map((n) => TOKEN_DEFS[n]),
  rejection: ['candidateFirstName', 'previousInterviewers', 'roleTitle', 'recruiterName'].map(
    (n) => TOKEN_DEFS[n]
  ),
  'talent-pool': ['candidateFirstName', 'roleTitle', 'recruiterName'].map((n) => TOKEN_DEFS[n]),
  'checking-in': ['candidateFirstName', 'bookingLink', 'roleTitle', 'recruiterName'].map(
    (n) => TOKEN_DEFS[n]
  ),
  internal: [
    'opening',
    'candidate',
    'what',
    'interviewerNames',
    'news',
    'need',
    'shifting',
    'close',
    'slots',
    'options',
    'moveNote',
    'agenda',
    'debrief',
    'logistics',
    'day',
    'mode',
    'checkIn',
    'candidateName',
    'roleTitle',
    'stageName',
    'recruiterName',
  ].map((n) => TOKEN_DEFS[n]),
  'outreach-inmail': [
    'greeting',
    'candidateFirstName',
    'personalLine',
    'roleTitle',
    'rolePitch',
    'whereLine',
    'location',
    'workModel',
    'salary',
    'jobLink',
    'bookingLink',
    'signOff',
    'recruiterName',
  ].map((n) => TOKEN_DEFS[n]),
  'outreach-follow-up': ['candidateFirstName', 'roleTitle', 'recruiterName'].map((n) => TOKEN_DEFS[n]),
};

export interface EmailTemplate {
  /** Composite Firestore doc id (scope+category+key, +owner for personal). */
  id: string;
  key: string;
  scope: Scope;
  category: Category;
  /** Owning recruiter's email for personal templates; null for company. */
  owner: string | null;
  label: string;
  order: number;
  subjectDe: string;
  subjectEn: string;
  de: string;
  en: string;
  active: boolean;
  source?: 'default' | 'custom';
  updatedBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** What the save form submits — the proxy owns id/owner/source/timestamps. */
export type EmailTemplateDraft = Pick<
  EmailTemplate,
  'key' | 'scope' | 'category' | 'label' | 'order' | 'subjectDe' | 'subjectEn' | 'de' | 'en' | 'active'
>;

/** What a force-reseed did — surfaced so a "nothing changed" is never a mystery. */
export interface ReseedSummary {
  ran: boolean;
  fromVersion: number;
  toVersion: number;
  seeded: number;
  failures: string[];
}

export class EmailTemplateError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'EmailTemplateError';
    this.status = status;
  }
}

async function readError(res: Response, fallback: string): Promise<never> {
  let message = fallback;
  try {
    const body = (await res.json()) as { error?: string };
    if (body?.error) message = body.error;
  } catch {
    // Non-JSON error body — keep the fallback.
  }
  throw new EmailTemplateError(message, res.status);
}

export async function fetchEmailTemplates(): Promise<EmailTemplate[]> {
  const res = await authenticatedFetch(BASE);
  if (!res.ok) await readError(res, 'Could not load the email template library');
  const data = (await res.json()) as { templates: EmailTemplate[] };
  return data.templates;
}

/**
 * One COMPANY template, rendered server-side with the given token values.
 *
 * Company only, deliberately (the /resolve route enforces it): board features must not depend on
 * someone's personal template. Tokens NOT passed in `vars` stay verbatim in the body (`{slots}`),
 * which callers use to substitute fast-changing values locally instead of re-fetching per change.
 * Throws on any failure so a react-query error path can drive an in-code fallback.
 */
export async function resolveEmailTemplate({
  category,
  key,
  lang = 'de',
  vars = {},
}: {
  category: Category;
  key: string;
  lang?: 'de' | 'en';
  vars?: Record<string, string>;
}): Promise<{ subject: string; body: string; label: string }> {
  const params = new URLSearchParams({ category, key, lang, ...vars });
  const res = await authenticatedFetch(`${BASE}/resolve?${params.toString()}`);
  if (!res.ok) await readError(res, 'Could not resolve the email template');
  return (await res.json()) as { subject: string; body: string; label: string };
}

export async function saveEmailTemplate(draft: EmailTemplateDraft): Promise<EmailTemplate> {
  const res = await authenticatedFetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(draft),
  });
  if (!res.ok) await readError(res, 'Could not save the template');
  const data = (await res.json()) as { template: EmailTemplate };
  return data.template;
}

export async function deleteEmailTemplate(id: string): Promise<void> {
  const res = await authenticatedFetch(`${BASE}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok) await readError(res, 'Could not delete the template');
}

/**
 * Force-restore the company templates to the current canonical copy, bypassing
 * the version gate. Overwrites company templates (that is the point); personal
 * ones are untouched.
 */
export async function reseedEmailTemplates(): Promise<ReseedSummary> {
  const res = await authenticatedFetch(`${BASE}/reseed`, { method: 'POST' });
  if (!res.ok) await readError(res, 'Could not restore the company templates');
  return (await res.json()) as ReseedSummary;
}

/**
 * Per-template tokens for the internal category (mirrors the proxy's TOKENS_BY_INTERNAL_KEY).
 *
 * The editor picks by template KEY here, not by category: a feedback nudge has no agenda, and
 * offering {agenda} in its menu would put a token in the message that its builder never fills —
 * `fmt` leaves an unknown token verbatim, so a literal "{agenda}" would reach a colleague's Slack.
 */
export const TOKENS_BY_INTERNAL_KEY: Record<string, TokenDef[]> = {
  'nudge-feedback': ['opening', 'candidate', 'what', 'candidateName', 'roleTitle', 'stageName', 'recruiterName'].map(
    (n) => TOKEN_DEFS[n]
  ),
  'ask-panelists': [
    'roleTitle',
    'interviewerNames',
    'news',
    'need',
    'shifting',
    'slots',
    'close',
    'candidateCount',
    'slotCount',
    'moveCount',
  ].map((n) => TOKEN_DEFS[n]),
  'final-round-ask': ['roleTitle', 'candidateName', 'recruiterName', 'options', 'moveNote'].map(
    (n) => TOKEN_DEFS[n]
  ),
  'final-round-coordination': [
    'roleTitle',
    'candidateName',
    'day',
    'mode',
    'agenda',
    'debrief',
    'logistics',
    'checkIn',
  ].map((n) => TOKEN_DEFS[n]),
};
